import React from 'react'
import { Box, Button, Chip, Container, Grid, LinearProgress, Skeleton, Stack, Typography } from '@mui/material'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'

import {
  GeneralFreeIcon,
  GeneralOrganizationIcon,
  GeneralTeamIcon,
} from '../TeamSeatIcons'

import PlanComparisonTable from './PlanComparisonTable'

import { usePermissionsV2 } from '@/hooks/usePermissionsV2'
import { useUserCredits } from '@/hooks/useCredits'
import { useDynamicPricingData } from '@/hooks/useDynamicPricingData'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import { setCreateTeamModalOpen } from '@/Redux/appStateSlice'
import CalendlyDialog from '../ContactSalesDialog'
import { useState } from 'react'

interface PriceRow {
  label: string
  icon: React.ReactElement
  iconColor: string
  price: string
  unit: string
  credits?: string
  sub?: string
}

interface PricingPlan {
  id: string
  label: string
  icon: React.ReactElement
  iconBg: string
  badge?: string | null
  badgeBg?: string
  badgeColor?: string
  description: string
  buttonLabel: string
  buttonSub: string
  priceRows: PriceRow[]
  features: string[]
}

// Add type definition for PlanCard
interface PlanCardData {
  icon: React.ReactElement
  name: string
  description: string
  creditsLabel: string
  showButton: boolean
  buttonLabel: string
  creditsText: string
  rightLabel: string | null
  isUnlimited: boolean
  progress: number
}

// Hardcoded pricing plans removed - now using dynamic data from useDynamicPricingData hook

export function PricingPageComponent() {
  const dispatch = useDispatch()
  return (
    <Box className="Container" sx={{ backgroundColor: '#fff', position: 'relative' }}>
      <Container maxWidth="xl">
        <BackgroundCover style={{ position: 'absolute', top: -10, left: 0, right: 0, bottom: 0, zIndex: 0 }} />
        <Grid container spacing={4}>
          <Grid item  xs={12}>
            <Stack alignItems="center" direction="column" gap="24px" p="96px 0px" zIndex={1}>
              <Typography fontSize={48} fontWeight={600} lineHeight="60px" textAlign="center" zIndex={1}>
                Streamline{' '}
                <span
                  style={{
                    WebkitTextFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    backgroundImage: 'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)',
                  }}
                >
                  grading
                </span>{' '}
                & feedback.{' '}
              </Typography>
              <Typography color="#475467" fontSize={20} fontWeight={400} lineHeight="30px">
                Simple, transparent pricing that grows with you.
              </Typography>
            </Stack>
          </Grid>
            <PricingCards />
          <PlanComparisonTable
            onChatToSales={() => {
              console.log('Chat to sales')
            }}
            onGetStarted={() => {
              console.log('Get started')
              dispatch(setCreateTeamModalOpen(true))
            }}
          />
        </Grid>
      </Container>
    </Box>
  )
}

interface PlanCardProps {
  plan: PricingPlan
  showFeatures?: boolean
  activePlanId?: string
  onGetStarted: () => void
  onTalkToSales: () => void
}

function PlanCard({
  plan,
  showFeatures = true,
  activePlanId,
  onGetStarted = () => { },
  onTalkToSales = () => { }
}: PlanCardProps) {
  const isActive = plan.id === activePlanId 

  return (
    <Grid item lg={4} md={4} sm={4} xs={12}>
      <Box
        sx={{
          flex: 1,
          height: '100%',
          width: '100%',
          mx: 'auto',
          borderRadius: '16px',
          border: '1px solid #EAECF0',
          background: '#FFF',
          boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
        }}
      >
        {/* Header */}
        <Stack
          alignItems="flex-start"
          borderBottom="1px solid #EAECF0"
          flexDirection="column"
          gap="32px"
          minHeight="520px"
          padding="32px"
        >
          <Stack alignItems="flex-start" direction="row" justifyContent="space-between" width="100%">
            {plan.icon}
            {plan.badge ? (
              <Chip
                label={plan.badge}
                sx={{
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  border: '1px solid #FCCEEE',
                  background: '#FDF2FA',
                  color: '#A12F7E',
                  textAlign: 'center',
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                  height: 28,
                }}
                variant="outlined"
              />
            ) : null}
          </Stack>
          {/* Title and Price */}
          <Stack alignItems="flex-start" flex="1 0 0" flexDirection="column" gap="6px">
            <Typography color="#475467" fontSize={16} fontWeight={600} lineHeight="24px">
              {plan.label}
            </Typography>
            <Typography color="#475467" fontSize={14} fontWeight={400} lineHeight="20px">
              {plan.description}
            </Typography>
          </Stack>
          {/* Pricing Rows */}
          <Stack flex="1 1 auto" width="100%">
            {plan.priceRows.length > 0 && (
              <Stack alignItems="flex-start" flexDirection="column" gap="8px" width="100%">
                {plan.priceRows.map((row, i) => (
                  <Stack
                    key={`${row.label}-${i.toString()}`}
                    alignItems="center"
                    direction="row"
                    gap="8px"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Stack>{row.icon}</Stack>
                    <Stack alignItems="flex-start" flex="1 1 auto">
                      <Typography fontSize={14} fontWeight={500} lineHeight="20px">
                        {row.label}
                      </Typography>
                      <Typography color="#344054" fontSize={12} lineHeight="18px">
                        {row.credits || ''}
                      </Typography>
                      {/* Subtext for student seat */}
                      {plan.id === 'organization' && row.sub && row.label === 'Student License' ? (
                        <Typography color="#344054" fontSize={12} lineHeight="24px">
                          {row.sub}
                        </Typography>
                      ) : null}
                    </Stack>
                    <Stack direction="row" gap="4px" justifyContent="center">
                      <Typography color="#101828" fontSize={14} fontWeight={600} lineHeight="24px">
                        {row.price}
                      </Typography>
                      <Typography color="#344054" fontSize={12} lineHeight="24px">
                        {row.unit}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>

          {/* Actions */}
          <Stack gap="12px" width="100%">
            <Button
              fullWidth
              disabled={isActive}
              sx={{ height: 48, fontSize: 16, fontWeight: 600, lineHeight: '24px' }}
              variant="contained"
              onClick={plan.id === 'organization' ? onTalkToSales :onGetStarted}
            >
              {isActive ? 'Current' : plan.id === 'organization' ? 'Talk to Sales' : plan.buttonLabel}
            </Button>
            <Button
              
              color="primary"
              sx={{ height: 44, fontSize: 14, fontWeight: 600, lineHeight: '20px' }}
              variant="text"
            >
              {plan.buttonSub}
            </Button>
          </Stack>
        </Stack>
        {/* Features */}
        {showFeatures ? (
          <Stack
            sx={{
              display: 'flex',
              padding: '32px 32px 40px 32px',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '24px',
              flex: '1 1 auto',
            }}
          >
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '4px',
              }}
            >
              <Typography color="#101828" fontSize={16} fontWeight={600} lineHeight="24px">
                Features
              </Typography>
              <Stack direction="column" gap="16px">
                {plan.features.map((feature, i) => (
                  <Stack key={`${feature}-${i.toString()}`} alignItems="flex-start" direction="row" gap="12px">
                    <CheckCircleOutlineRoundedIcon sx={{ color: '#EB3EB7' }} />
                    <Typography color="#475467" fontSize={16} fontWeight={400} lineHeight="24px">
                      {feature}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Stack>
        ) : null}
      </Box>
    </Grid>
  )
}

export function PricingCards({ showFeatures = true }: { showFeatures?: boolean }) {
  const { allocationSource } = usePermissionsV2()
  const [talkToSalesOpen, setTalkToSalesOpen] = useState(false )
  const { pricingPlans, isLoading } = useDynamicPricingData()
  let activePlanId: string | undefined
  const dispatch = useDispatch()
  if (allocationSource) {
    if (allocationSource === 'free_tier') {
      activePlanId = 'individual'
    } else if (allocationSource === 'team_seat') {
      activePlanId = 'team'
    } else if (allocationSource === 'organization') {
      activePlanId = 'organization'
    }
  }

  if (isLoading) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3].map(index => (
          <Grid key={index} item sm={4} xs={12} >
            <Stack height="520px">
            <Skeleton
              sx={{
                height: '100%',
                borderRadius: '16px',
                border: '1px solid #EAECF0',
                width: '100%',
              }}
              />
               </Stack>
          </Grid>
        ))}
      </Grid>
    )
  }

  return (
    <>
      <CalendlyDialog open={talkToSalesOpen} onClose={() => {setTalkToSalesOpen(false)}} /> 
      {pricingPlans.map(plan => (
        <PlanCard
          key={plan.id}
          activePlanId={activePlanId}
          onGetStarted={() => {
            dispatch(setCreateTeamModalOpen(true))
          }}
          onTalkToSales={() => {
            setTalkToSalesOpen(true)
          }}  
          plan={plan}
          showFeatures={showFeatures}
        />
      ))}
    </>
  )
}

interface AICreditPackagesProps {
  onGetCredits: () => void
}

export function AICreditPackages({ onGetCredits }: AICreditPackagesProps): JSX.Element {
  const { data: userCredits, isLoading,  } = useUserCredits()
  
  const { allocationSource } = usePermissionsV2()

  if (isLoading) {
    return (
      <Box bgcolor="#FFF" borderRadius="16px" p={4} sx={{ border: '1px solid #EAECF0', textAlign: 'center' }}>
        <Typography fontSize={18} fontWeight={500}>
          Loading plan details...
        </Typography>
      </Box>
    )
  }

  if (!userCredits) {
    return (
      <Box bgcolor="#FFF" borderRadius="16px" p={4} sx={{ border: '1px solid #EAECF0', textAlign: 'center' }}>
        <Typography fontSize={18} fontWeight={500}>
          No plan information available
        </Typography>
      </Box>
    )
  }

  // Map effectivePlanId to plan type and card data
  let planCard: PlanCardData | null = null
  const planId = userCredits?.effectivePlanId
  const isUnlimited = userCredits?.isUnlimited || false
  // Monthly Credits
  const totalCredits = userCredits?.creditsAllocatedThisPeriod || 0
  const availableCredits = userCredits?.creditsAvailable|| 0

  // Top-Off Credits
  const topOffCreditsAvailable = userCredits?.topOffCreditsAvailable || 0
  const topOffCreditsTotal = userCredits?.topOffCreditsTotal || 0
  const topOffCreditsProgress = isUnlimited ? 1 : Math.min(topOffCreditsAvailable / Math.max(topOffCreditsTotal, 1), 1)

  // Progress
  const progress = isUnlimited ? 1 : Math.min(availableCredits / Math.max(totalCredits, 1), 1)

  if (
    allocationSource === 'free_tier') {
    planCard = {
      icon: <GeneralFreeIcon style={{ fontSize: 40 }} />,
      name: 'Free',
      description: 'You can purchase additional AI credits at any time',
      creditsLabel: 'AI Credits',
      showButton: true,
      buttonLabel: 'Get More Credits',
      creditsText: `${availableCredits.toLocaleString()}/${totalCredits.toLocaleString()}`,
      rightLabel: (planId ? planId.split('_').join(' ') : `Free tier`),
      isUnlimited: false,
      progress,
    }
  } else if (allocationSource === 'team_seat') {
    planCard = {
      icon: <GeneralTeamIcon style={{ fontSize: 40 }} />,
      name: 'Team Plan',
      description: 'A shared workspace to manage members, plans, and workflows',
      creditsLabel: 'AI Credits',
      showButton: true,
      buttonLabel: 'Get More Credits',
      creditsText: `${availableCredits}/${totalCredits}`,
      rightLabel: 'Core seat',
      isUnlimited: false,
      progress,
    }
  } else if (allocationSource === 'organization') {
    planCard = {
      icon: <GeneralOrganizationIcon style={{ fontSize: 40 }} />,
      name: 'Organization Plan',
      description: 'Multiple teams under one shared system for plans, seats, and permissions',
      creditsLabel: 'AI Credits',
      showButton: false,
      buttonLabel: '',
      creditsText: 'Unlimited',
      rightLabel: null,
      isUnlimited: true,
      progress: 1,
    }
  }

  if (!planCard) {
    return (
      <Box bgcolor="#FFF" borderRadius="16px" p={4} sx={{ border: '1px solid #EAECF0', textAlign: 'center' }}>
        <Typography fontSize={18} fontWeight={500}>
          Plan information not available
        </Typography>
      </Box>
    )
  }

  return (
    <Stack bgcolor="#FFF" borderRadius="16px" direction="row" gap={8} p="20px" sx={{ border: '1px solid #EAECF0' }}>
      {/* Left: Icon and plan info */}
      <Stack alignItems="flex-start" flex={1} gap="4px" padding="0px 12px" width="272px">
        <Box>{planCard.icon}</Box>
        <Box>
          <Typography color="#101828" fontSize={18} fontWeight={600} lineHeight="28px">
            {planCard.name}
          </Typography>
          <Typography color="#475467" lineHeight="20px">
            {planCard.description}
          </Typography>
        </Box>
      </Stack>
      {/* Right: AI Credits section */}
      <Stack bgcolor="#F9FAFB" borderRadius="12px" flex={1.5} gap="20px" justifyContent="center" padding="20px">
        <Box alignItems="center" display="flex" justifyContent="space-between" width="100%">
          <Stack alignItems="center" direction="row" gap="4px">
            <StarsIcon />
            <Typography color="#101828" fontSize={18} fontWeight={600} lineHeight="28px">
              {planCard.creditsLabel}
            </Typography>
          </Stack>
          {planCard.showButton ? (
            <Button
              color="secondary"
              sx={{ flexShrink: 0, alignSelf: 'center', width: 163, height: 36, fontWeight: 500 }}
              variant="outlined"
              onClick={onGetCredits}
            >
              {planCard.buttonLabel}
            </Button>
          ) : null}
        </Box>
        <Stack gap="4px" width="100%">
          {/* Progress bar or unlimited */}
          <Stack flex={1}>
            {planCard.isUnlimited ? (
              <Box>
                <LinearProgress
                  sx={{
                    height: 12,
                    borderRadius: 5,
                    background: 'linear-gradient(90deg, #FF6C6C 0%, #DD7BFF 100%)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #FF6C6C 0%, #DD7BFF 100%)',
                    },
                  }}
                  value={100}
                  variant="determinate"
                />
              </Box>
            ) : (
              <Box>
                <LinearProgress
                  sx={{
                    height: 12,
                    borderRadius: 5,
                    background: '#F2F4F7',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #FF6C6C 0%, #DD7BFF 100%)',
                    },
                  }}
                  value={planCard.progress * 100}
                  variant="determinate"
                  />
 
              </Box>
            )}
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="#475467" fontSize={12} fontWeight={500} lineHeight="18px" textTransform="capitalize">
              {planCard.rightLabel}
            </Typography>
            <Typography color="#475467" fontSize={12} fontWeight={500} lineHeight="18px">
              {planCard.creditsText}
            </Typography>
          </Stack>
          {topOffCreditsAvailable && !isUnlimited ? (
            <Stack direction="column" gap="4px" mt="12px">
              {/* <LinearProgress
                sx={{
                  height: 12,
                  borderRadius: 5,
                  background: '#F2F4F7',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #FF6C6C 0%, #DD7BFF 100%)',
                  },
                }}
                value={topOffCreditsProgress * 100}
                variant="determinate"
              /> */}
              <Stack direction="row" justifyContent="space-between">
                <Typography color="#475467" fontSize={12} fontWeight={500} lineHeight="18px">
                  Top-Off Credits
                </Typography>
                <Typography color="#475467" fontSize={12} fontWeight={500} lineHeight="18px">
                  {topOffCreditsAvailable.toLocaleString()}
                </Typography>
              </Stack>
            </Stack>
                  ) : null}
        </Stack>
      </Stack>
    </Stack>
  )
}

function BackgroundCover(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={225} viewBox="0 0 584 225" width={584} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M548.115 -283.139C559.678 -284.143 571.314 -280.289 578.072 -272.25C587.995 -260.451 583.65 -244.111 568.366 -235.747C557.364 -229.729 546.588 -223.115 536.333 -216.085C500.427 -191.483 469.94 -161.228 447.149 -127.739C466.386 -114.73 482.568 -98.7895 493.91 -81.2804C512.225 -53.0105 518.309 -19.8008 511.043 12.2272C501.794 52.9927 474.889 66.541 453.942 70.7285C432.06 75.1018 407.775 70.7274 388.993 59.02C375.877 50.8445 365.458 39.5429 357.142 24.4756C331.996 -21.0946 343.488 -65.9851 363.118 -105.917C342.297 -111.628 319.722 -112.237 298.075 -105.155C298.021 -105.139 297.967 -105.121 297.912 -105.105C262.641 -93.7356 229.811 -75.1808 201.383 -51.9462C215.696 -32.3376 225.004 -9.1739 228.539 16.4107C235.212 64.706 221.051 117.609 187.575 169.506C169.641 206.305 134.484 227.821 97.5302 224.423C78.6285 222.688 61.8503 214.324 50.2865 200.874C39.8579 188.747 33.9814 173.212 32.8232 154.7C32.8192 154.654 32.8151 154.607 32.8148 154.563C29.4489 94.5041 51.6116 30.0747 95.2245 -26.8532C101.443 -34.9698 108.019 -42.8228 114.93 -50.3935C114.754 -50.4939 114.585 -50.5922 114.41 -50.6899C93.3209 -62.7339 67.0944 -67.0031 38.2443 -63.1429C37.5402 -46.9867 33.3884 -30.9882 25.824 -15.9466C9.10651 17.2987 -22.8115 43.9431 -66.5079 61.1252C-96.4904 73.5224 -129.792 72.2829 -151.481 57.9389C-171.681 44.5784 -178.009 22.6817 -168.405 -0.634187C-168.31 -0.862768 -168.211 -1.08893 -168.109 -1.31811C-160.967 -17.2127 -147.581 -29.2648 -134.182 -39.9245C-134.101 -39.9867 -134.023 -40.0513 -133.941 -40.1135C-101.206 -65.65 -68.0371 -84.9503 -34.8026 -97.8392C-49.6588 -129.791 -89.646 -161.018 -157.748 -149.872C-175.731 -146.933 -192.705 -156.027 -195.679 -170.184C-198.654 -184.343 -186.494 -198.203 -168.521 -201.144C-121.002 -208.914 -76.3702 -203.136 -39.4374 -184.423C-7.99505 -168.497 16.0054 -143.819 28.6076 -114.601C75.4315 -121.154 118.762 -113.95 154.358 -93.6254C155.81 -92.7957 157.241 -91.9506 158.654 -91.0845C193.346 -118.51 233.004 -139.885 275.19 -153.488C311.983 -165.499 352.744 -165.632 391.399 -154.072C417.98 -192.291 453.142 -226.854 494.399 -255.122C506.565 -263.46 519.364 -271.317 532.441 -278.47C537.312 -281.135 542.706 -282.669 548.115 -283.139ZM-104 17.1999C-101.708 17.0009 -98.625 16.3723 -94.9451 14.8327C-94.6695 14.7179 -94.3902 14.6055 -94.1106 14.4959C-59.2052 0.836701 -40.4368 -19.0969 -32.4164 -39.7966C-51.0347 -30.2866 -69.8921 -18.1559 -88.5535 -3.60759C-95.5182 1.93533 -103.125 8.30791 -105.916 14.1467C-106.426 15.4578 -106.677 16.4904 -106.786 17.238C-106.068 17.2913 -105.13 17.298 -104 17.1999ZM439.612 20.052C440.712 18.9237 443.859 14.9737 446.124 4.99986C450.851 -15.8462 446.892 -37.4556 434.972 -55.8508C430.547 -62.6852 424.94 -69.2572 418.416 -75.3254C407.78 -49.4191 403.963 -23.1465 417.755 1.84486C421.78 9.14074 426.249 14.3519 431.039 17.3373C434.334 19.3921 437.751 20.1145 439.612 20.052ZM107.793 172.36C110.691 171.833 119.637 166.333 126.608 151.437C127.096 150.394 127.662 149.376 128.311 148.386C167.657 88.0442 171.684 33.5842 154.913 -5.0155C117.554 41.7493 95.7854 97.3229 98.7233 150.046C99.7178 165.631 105.882 171.447 107.793 172.36Z"
        fill="#EE46BC"
      />
    </svg>
  )
}

function StarsIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={16} viewBox="0 0 16 16" width={16} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M12.334 5.33337V2.00004M3.66732 14V10.6667M10.6673 3.66671H14.0007M2.00065 12.3334H5.33399M4.33398 1.33337L3.81101 2.37932C3.63402 2.7333 3.54552 2.9103 3.4273 3.06367C3.32239 3.19977 3.20038 3.32178 3.06428 3.42669C2.91091 3.54491 2.73391 3.63341 2.37993 3.8104L1.33398 4.33337L2.37993 4.85635C2.73391 5.03334 2.91091 5.12184 3.06428 5.24006C3.20038 5.34497 3.32239 5.46698 3.4273 5.60308C3.54552 5.75645 3.63402 5.93344 3.81101 6.28743L4.33399 7.33338L4.85696 6.28743C5.03395 5.93344 5.12245 5.75645 5.24067 5.60308C5.34558 5.46698 5.46759 5.34497 5.60369 5.24006C5.75706 5.12184 5.93406 5.03334 6.28804 4.85635L7.33399 4.33337L6.28804 3.8104C5.93406 3.63341 5.75706 3.54491 5.60369 3.42669C5.46759 3.32178 5.34558 3.19977 5.24067 3.06367C5.12245 2.9103 5.03395 2.7333 4.85696 2.37932L4.33398 1.33337ZM11.334 8.00004L10.6999 9.26821C10.5229 9.62219 10.4344 9.79919 10.3162 9.95256C10.2113 10.0887 10.0893 10.2107 9.95317 10.3156C9.7998 10.4338 9.62281 10.5223 9.26882 10.6993L8.00065 11.3334L9.26882 11.9675C9.62281 12.1445 9.7998 12.2329 9.95317 12.3512C10.0893 12.4561 10.2113 12.5781 10.3162 12.7142C10.4344 12.8676 10.5229 13.0446 10.6999 13.3985L11.334 14.6667L11.9681 13.3985C12.1451 13.0446 12.2336 12.8676 12.3518 12.7142C12.4567 12.5781 12.5787 12.4561 12.7148 12.3512C12.8682 12.2329 13.0452 12.1445 13.3992 11.9675L14.6673 11.3334L13.3992 10.6993C13.0452 10.5223 12.8682 10.4338 12.7148 10.3156C12.5787 10.2107 12.4567 10.0887 12.3518 9.95256C12.2336 9.79919 12.1451 9.6222 11.9681 9.26821L11.334 8.00004Z"
        stroke="url(#paint0_linear_194_38782)"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.6}
      />
      <defs>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id="paint0_linear_194_38782"
          x1={1.33378}
          x2={14.6672}
          y1={14.6666}
          y2={1.33321}
        >
          <stop stopColor="#FF6C6C" />
          <stop offset={1} stopColor="#DD7BFF" />
        </linearGradient>
      </defs>
    </svg>
  )
}
